import _ from "lodash";
import ko from "knockout";
import hogan from "hogan.js";
import moment from "moment";
import momentUtils from "util/momentUtils";
import phoneformat from "phoneformat.js";
import defaultTags from "extensions/i18n/defaultTags";
import loggingService from "services/loggingService";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import {
    DateTimeHelper,
    TIME_MINUTES_SECONDS_MILLISECONDS_OPTIONS,
    TIME_MINUTES_SECONDS_OPTIONS,
    MEDIUM_TIME_OPTIONS,
} from "util/DateTimeHelper";
import defaultStrings from "strings/en-US.json";
import "extensions/i18n/knockout.lString";
import "extensions/i18n/knockout.lAttr";
import "extensions/i18n/knockout.lDate";
import "extensions/i18n/knockout.lDuration";
import "extensions/i18n/knockout.lTemplate";
import "extensions/i18n/knockout.lNumber";

let appLocale; // Stores the locale of the application that has been explicitly set
let appTimezone; // Stores the timezone of the application that has been explicitly set
const compiledTags = {}, // Compiled tag templates
    compiledStrings = {}; // Container for compiled string templates

/**
 * Compiles string templates and merges them into an existing collection of
 * compiled templates.
 *
 * @param {Object} uncompiledObject Key value pairs of the form stringId -> uncompiledTemplate.
 * @param {Object} compiledObject Key value pairs of the form stringId -> compiledTemplate. Entries
 *        from uncompiledObject will be merged with this collection.
 */
const compileAndMerge = function (uncompiledObject, compiledObject) {
    for (const key in uncompiledObject) {
        if (uncompiledObject.hasOwnProperty(key)) {
            compiledObject[key] = hogan.compile(uncompiledObject[key]);
        }
    }
};

/**
 * Gets the currently set locale.
 * @return {String} the current locale in use.
 */
const getCurrentLocale = function () {
    return appLocale;
};

/**
 * Extract a number when the SIP address is like
 * sip:+18257567163@127.0.0.1
 */
const extractSipNumber = function (sipAddress) {
    let num = sipAddress.substr(4);
    const stripAfter = num.indexOf("@");
    num = num.substr(0, stripAfter);
    return num;
};

/**
 * Extract a SIP address where the format is like:
 * sip:jerry@inin.com
 * sip:jerrysSIP@127.0.0.1;someparam=user
 */
const extractSipAddress = function (sipAddress) {
    let address = sipAddress.substr(4);
    const stripAfter = address.indexOf(";");
    if (stripAfter > 0) {
        address = address.substr(0, stripAfter);
    }
    return address;
};

/**
 * format an international number
 * @param  {String} num - a string representation of a phone number
 * @return {String}     - The formatted string
 */
const formatInternationalNumber = function (num) {
    const phoneUtil =
        phoneformat.i18n.phonenumbers.PhoneNumberUtil.getInstance();

    const parsedNumber = phoneUtil.parse(num);
    const formattedNumber = phoneUtil.format(parsedNumber, 1); // 1 is for International
    return formattedNumber;
};

/**
 * Core internationalization object. Provides methods for
 * choosing locale and performing basic localization tasks.
 * @exports extensions/i18n/knockout-i18n
 */
ko.i18n = {
    /**
     * format a phone address
     * @param  {String} number - a string representation of a phone address
     * @return {String} formattedAddress - a formatted String.
     */
    formatPhoneAddress: function (number) {
        if (!number) {
            return;
        }

        number = number + ""; //coerce to string

        let formattedPhoneAddress;

        try {
            if (number.indexOf("sip:") === 0) {
                if (number.indexOf("+") === 4) {
                    formattedPhoneAddress = formatInternationalNumber(
                        extractSipNumber(number)
                    );
                } else {
                    return extractSipAddress(number);
                }
            } else if (number.indexOf("tel:") === 0) {
                formattedPhoneAddress = formatInternationalNumber(
                    number.substring(4)
                );
            } else if (number.indexOf("+") === 0) {
                formattedPhoneAddress = formatInternationalNumber(number);
            }
        } catch (e) {
            loggingService.debug(
                "Failed to format phone number: " + number + ". " + e
            );
        }

        if (!formattedPhoneAddress) {
            return number;
        }

        return formattedPhoneAddress;
    },
    /**
     * Sets the locale the application should use for localization. Default
     * locale on initialization is "en-US".
     *
     * @param {String} locale The locale to use, format should be xx-XX.
     */
    setLocale: function (locale) {
        appLocale = locale || "en-US"; // if locale is not set or is an empty string, default to "en-US"
        moment.locale(momentUtils.normalizeMomentLocale(appLocale));
    },

    /**
     * Get the currently set locale.
     */
    getLocale: function () {
        return appLocale;
    },
    /**
     * Sets the timezone the application should use.
     *
     * @param {String} timezone The timezone to use.
     */
    setTimezone: function (timezone) {
        appTimezone = timezone;
        moment.tz.setDefault(timezone);
    },

    /**
     * Get the currently set timezone (or) set the LocalTimeZone
     */
    getTimezone: function () {
        return appTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    },
    /**
     * Registers string templates for use within a specific locale.
     *
     * @param {Object} strings Key value pairs of stringId -> string template
     * @param {String} [locale] The locale to register the strings under,
     *        defaults to the currently set locale. Format should be xx-XX.
     */
    registerStrings: function (strings, locale) {
        const registerLocale = locale || getCurrentLocale();
        let compiledLocaleStrings = compiledStrings[registerLocale];

        if (!compiledLocaleStrings) {
            compiledLocaleStrings = compiledStrings[registerLocale] = {};
        }

        compileAndMerge(strings, compiledLocaleStrings);
    },

    /**
     * Registers html tags for use within string templates.
     *
     * @param {Object} tags Key value pairs of form tagId -> tagString
     */
    registerTags: function (tags) {
        compileAndMerge(tags, compiledTags);
    },

    /**
     * Renders a named string template.
     *
     * @param {Object}  The object that contains named properties name (required),
     *                  data (optional), and locale (optional).
     *
     * -- or --
     *
     * @param {String}  The name of the string to be rendered.
     * @param {Object}  Data used for token replacement within the string.
     * @param {String}  Locale to render the string in. Defaults to the current set locale.
     *
     * @return {String} The rendered string, or an empty string if no localized string is found corresponding
     *         to the provided name.
     */
    renderString: function (...args) {
        let rawName, rawData, rawLocale;

        if (_.isPlainObject(ko.unwrap(args[0]))) {
            const obj = ko.unwrap(args[0]);
            rawName = ko.unwrap(obj.name);
            rawData = ko.unwrap(obj.data);
            rawLocale = ko.unwrap(obj.locale);
        } else {
            rawName = ko.unwrap(args[0]);
            rawData = ko.unwrap(args[1]);
            rawLocale = ko.unwrap(args[2]);

            // data parameter is optional
            if (!rawLocale && typeof rawData === "string") {
                rawLocale = rawData;
                rawData = undefined;
            }
        }

        const renderLocale = rawLocale || getCurrentLocale();
        let string = compiledStrings[renderLocale]
            ? compiledStrings[renderLocale][rawName]
            : null;

        // Fall through to English.
        if (!string) {
            string = compiledStrings["en-US"]
                ? compiledStrings["en-US"][rawName]
                : null;
        }

        if (!string) {
            loggingService.error(rawName + " is not localized.");
            return "";
        }
        return string.render(rawData, compiledTags);
    },

    /**
     * Wrapper method around Intl.NumberFormat.
     */
    formatNumber: function (value, options, locale) {
        const localeToUse = locale || getCurrentLocale();
        return new Intl.NumberFormat(localeToUse, options).format(value);
    },

    /**
     * Date-formatting method using moment.js or Spark intl methods based on feature toggle.
     * Valid date formats for moment may differ from globalize.
     * See documentation at:
     * {@link http://momentjs.com/docs/#/displaying/format/}
     */
    formatDate: function (value, format, locale, intlOptions) {
        // Use Spark intl methods if pure-2946 feature toggle is enabled
        if (
            featureToggleService.isEnabled(
                FeatureToggle.PURE_2946_INTERNATIONALIZATION_DATE_TIME
            )
        ) {
            try {
                return DateTimeHelper.formatDateWithTime(
                    value,
                    intlOptions,
                    appTimezone
                );
            } catch (error) {
                loggingService.warn(
                    "DateTimeHelper formatting failed, falling back to moment.js",
                    error
                ); // Fall through to moment.js implementation
            }
        }

        // Fallback to existing moment.js implementation
        if (format) {
            if (locale) {
                return moment(value)
                    .locale(momentUtils.normalizeMomentLocale(locale))
                    .format(format);
            }
            return moment(value).format(format);
        }
        return moment(value).format("L");
    },

    /**
     * Format a duration of time into a localized format that is easily consumable by
     * end users. This method will take a duration and perform the necessary roll-ups
     * of time according to the following logic:
     *
     * - if the duration is < 1 second, display "minutes:seconds.milliseconds"
     * - if the duration is >= 1 second and < 60 minutes, display "minutes:seconds"
     * - if the duration is >= 1 hour and <= 24 hours, display "hours:minutes:seconds"
     * - if the duration is >= 1 day and <= 365 days, display "x days"
     * - if the duration is >= 1 year, display "x years"
     *
     * @param  {number} duration The length of time, in milliseconds that will be used
     *  to create a localized, human readable string.
     * @return {String|null} A localized string representing the length of the time duration.
     *  If the provided duration argument is null or undefined, null will be returned.
     */
    formatDuration: function (duration) {
        const self = this;

        //
        // Private Methods
        //

        const formatAsTime = function (duration, messageKey, intlOptions) {
            // Since the formatting is based on a Date object, we create a moment that is set
            // to 00:00:00 (24 hour clock) and add the duration of time (in milliseconds) to
            // this moment object. This will create a Date object that has the correct hours,
            // minutes, and seconds values that we can use to render a string for display.
            const date = moment()
                .startOf("day")
                .add(duration, "milliseconds")
                .toDate();
            return self.formatDate(
                date,
                self.renderString(messageKey),
                null,
                intlOptions
            );
        };

        const formatAsString = function (
            durationAmt,
            singularString,
            pluralString
        ) {
            let messageKey = pluralString;

            if (durationAmt === 1) {
                messageKey = singularString;
            }

            return self.renderString(messageKey, { duration: durationAmt });
        };

        const formatAsDayString = function (durObj) {
            const days = Math.floor(durObj.asDays());
            return formatAsString(
                days,
                "timeDuration.day",
                "timeDuration.days"
            );
        };

        const formatAsYearString = function (durObj) {
            const years = Math.floor(durObj.asYears());
            return formatAsString(
                years,
                "timeDuration.year",
                "timeDuration.years"
            );
        };

        //
        // Main Logic
        //

        if (_.isNull(duration) || _.isUndefined(duration)) {
            return null;
        }

        const durObj = moment.duration(duration);

        if (durObj.asSeconds() < 1) {
            return formatAsTime(
                duration,
                "timeDuration.minutesSecondsMilliseconds",
                TIME_MINUTES_SECONDS_MILLISECONDS_OPTIONS
            );
        }

        if (durObj.asSeconds() >= 1 && durObj.asMinutes() < 60) {
            return formatAsTime(
                duration,
                "timeDuration.minutesSeconds",
                TIME_MINUTES_SECONDS_OPTIONS
            );
        }

        if (durObj.asHours() >= 1 && durObj.asHours() < 24) {
            return formatAsTime(
                duration,
                "timeDuration.hoursMinutesSeconds",
                MEDIUM_TIME_OPTIONS
            );
        }

        if (durObj.asDays() >= 1 && durObj.asDays() < 365) {
            return formatAsDayString(durObj);
        }

        return formatAsYearString(durObj);
    },

    formatDurationWithUnit: function (duration) {
        let year = "",
            day = "",
            hour = "",
            minute = "",
            second = "",
            milliSecond = "";
        if (_.isNull(duration) || _.isUndefined(duration)) {
            return [year, day, hour, minute, second, milliSecond];
        }

        const durObj = moment.duration(duration);

        if (durObj.years() >= 1) {
            year = durObj.years().toString();
        } else if (durObj.asDays() >= 1) {
            day = Math.floor(durObj.asDays()).toString();
        } else {
            if (durObj.hours() >= 1) {
                hour = durObj.hours().toString();
                minute = durObj.minutes().toString().padStart(2, "0");
            } else {
                minute = durObj.minutes().toString();
            }
            second = durObj.seconds().toString().padStart(2, "0");
            milliSecond = durObj.milliseconds().toString().padStart(3, "0");
        }

        return [year, day, hour, minute, second, milliSecond];
    },

    /**
     * Parses an integer that might be in a locale specific format.  truncating to an integer using Math.floor
     */
    parseInt: function (value, locale) {
        // ~~ truncates a number, removing the non-integer part
        return new Intl.NumberFormat(locale).format(Math.floor(value));
    },

    /**
     *  Parses a number that might be in a locale specific format
     */
    parseFloat: function (value, locale) {
        const localeToUse = locale || getCurrentLocale();
        return new Intl.NumberFormat(localeToUse).format(value);
    },

    /**
     * Wrapper method around Intl.DateTimeFormat
     */
    parseDate: function (value, format, locale) {
        const localeToUse = locale || getCurrentLocale();
        return new Intl.DateTimeFormat(localeToUse, format).format(value);
    },

    /**
     * Determines if a localization key exists.
     * @param key The localization key to check for.
     */
    keyExists: function (name, locale) {
        const renderLocale = locale || getCurrentLocale();
        if (
            compiledStrings[renderLocale] &&
            compiledStrings[renderLocale][name]
        ) {
            return true;
        }
        return false;
    },

    /**
     * Gets short name for locales weekdays
     * @returns Array of text strings ex: "Mo", "Tu" etc...
     */
    getShortDaysOfWeek: function (locale) {
        const renderLocale = locale || ko.i18n.getLocale();
        const formatter = new Intl.DateTimeFormat(renderLocale, {
            weekday: "short",
            timeZone: "UTC",
        });
        const days = [1, 2, 3, 4, 5, 6, 7].map((day) => {
            return new Date(Date.UTC(2017, 0, day));
        });
        return days.map((date) => formatter.format(date));
    },

    /**
     * Gets the abbreviated name of the locals months
     * @returns Array of text strings; ex: "Jan", "Feb", etc...
     */
    getShortMonths: function (locale) {
        const renderLocale = locale || ko.i18n.getLocale();
        const formatter = new Intl.DateTimeFormat(renderLocale, {
            month: "short",
            timeZone: "UTC",
        });
        const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => {
            return new Date(Date.UTC(2017, month, 0));
        });
        return months.map((date) => formatter.format(date));
    },

    /**
     * Gets the first day of chosen locale
     * @returns Integer to be used as index to return starting day of the week for locale
     */
    getFirstDay: function (locale) {
        const renderLocale = locale || ko.i18n.getLocale();
        const localeInfo = new Intl.Locale(renderLocale);
        const firstDayInfo = localeInfo.weekInfo ?? { firstDay: 7 }; // default to Sunday;
        // Translate Sunday from being 7 to 0 because our codes expects 0 but Intl API specifies it as 7
        return firstDayInfo.firstDay === 7 ? 0 : firstDayInfo.firstDay;
    },

    getDateFormatString: function () {
        const formatObj = new Intl.DateTimeFormat(
            ko.i18n.getLocale()
        ).formatToParts(new Date());
        return formatObj
            .map((obj) => {
                switch (obj.type) {
                    case "day":
                        return "DD";
                    case "month":
                        return "MM";
                    case "year":
                        return "YYYY";
                    default:
                        return obj.value;
                }
            })
            .join("");
    },

    calculateDuration: function (endTime, startTime) {
        const duration = moment
            .duration(moment(startTime).diff(moment(endTime)))
            .asMilliseconds();
        return duration < 0 ? 0 : duration;
    },

    getFormattedDuration: function (endTime, startTime) {
        const milliseconds = this.calculateDuration(endTime, startTime);
        return this.formatDurationWithUnit(milliseconds);
    },

    /**
     * Returns the relative time from now
     * @param {String} datetime - A string representation of the date and time
     * @param {String} locale - The locale to use, format should be xx-XX
     */
    fromNow: function (datetime, locale) {
        const localeToUse = locale || getCurrentLocale();
        return moment(datetime).locale(localeToUse).fromNow();
    },
};

// compile built-in tags;
ko.i18n.registerTags(defaultTags);

// compile default strings;
ko.i18n.registerStrings(defaultStrings, "en-US");

export default ko.i18n;
